import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, fmtDate, StatusBadge } from "@/components/shared";
import { bandColor } from "./Reports";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Save, CheckCircle2, Flag, History, Lock, StickyNote, Send } from "lucide-react";
import { toast } from "sonner";

export default function ValidationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [overall, setOverall] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/validation/${id}`).then((r) => {
    setData(r.data);
    setOverall(r.data.validation?.overall_comment || "");
  }).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (data === null) return <Loader label="Loading submission" />;
  if (data === false) return <div className="text-sm text-muted-foreground">Not available.</div>;

  const { submission, validation } = data;
  const vmap = {};
  (validation?.question_comments || []).forEach((c) => { vmap[c.question_id] = c; });

  const saveComment = async (question_id, comment, validator_score) => {
    try {
      await api.post(`/validation/${id}/comment`, { question_id, comment, validator_score });
      toast.success("Comment saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      if (overall !== (validation?.overall_comment || "")) await saveComment(null, overall, null);
      await api.post(`/validation/${id}/complete`);
      toast.success("Validation completed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const canValidate = ["validator", "am", "admin"].includes(user.role);

  return (
    <div>
      <PageHeader
        overline={`${submission.tac_org || submission.tac_name} · Version v${submission.version}`}
        title={submission.title}
        description="Review the exact submission snapshot under validation and record comments."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/validation")} className="rounded-md"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" /> Queue</Button>
            <CreateFollowupDialog submission={submission} onCreated={load} />
            {canValidate && (
              <Button onClick={complete} disabled={saving} className="rounded-md" data-testid="complete-validation-button">
                <CheckCircle2 className="h-4 w-4 mr-1.5" aria-hidden="true" /> Complete validation
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="version-under-review">
        <History className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Reviewing immutable snapshot <span className="font-mono font-semibold">v{submission.version}</span> submitted {fmtDate(submission.submitted_at)}.</span>
        <span className="ml-auto font-heading font-bold">Overall {submission.scores?.overall} · <span className={bandColor(submission.scores?.band)}>{submission.scores?.band}</span></span>
      </div>

      <div className="space-y-3">
        {submission.responses.map((r, i) => (
          <QuestionRow key={r.question_id} r={r} idx={i} existing={vmap[r.question_id]} canValidate={canValidate} onSave={saveComment} />
        ))}
      </div>

      <div className="mt-6 bg-card border border-border rounded-md p-5">
        <Label htmlFor="overall" className="overline">Overall validation comment</Label>
        <Textarea id="overall" value={overall} onChange={(e) => setOverall(e.target.value)} disabled={!canValidate}
          className="mt-2 rounded-md" placeholder="Summary of validation findings…" data-testid="overall-comment-input" />
        {canValidate && (
          <div className="mt-3 flex justify-end">
            <Button variant="outline" onClick={() => saveComment(null, overall, null)} className="rounded-md" data-testid="save-overall-comment">
              <Save className="h-4 w-4 mr-1.5" aria-hidden="true" /> Save overall comment
            </Button>
          </div>
        )}
      </div>

      {validation?.comment_history?.length > 0 && (
        <div className="mt-6 bg-card border border-border rounded-md p-5">
          <div className="overline mb-3">Comment change history (append-only)</div>
          <ul className="space-y-2">
            {validation.comment_history.slice().reverse().map((h, i) => (
              <li key={i} className="text-sm border-l-2 border-slate-200 pl-3">
                <span className="text-muted-foreground">{h.question_id ? "Question" : "Overall"}: </span>{h.comment}
                {h.validator_score != null && <span className="font-mono ml-1">(score {h.validator_score})</span>}
                <div className="text-xs text-muted-foreground font-mono">{h.by} · {fmtDate(h.at)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <InternalNotes tacId={submission.tac_id} tacName={submission.tac_name} />
    </div>
  );
}

function InternalNotes({ tacId, tacName }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/users/${tacId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tacId]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/users/${tacId}/notes`, { note: text });
      setText("");
      load();
      toast.success("Internal note added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 bg-card border border-border rounded-md p-5" data-testid="internal-notes">
      <div className="flex items-center gap-2 mb-1">
        <StickyNote className="h-4 w-4 text-[#303E49]" aria-hidden="true" />
        <h3 className="font-heading font-semibold">Internal reviewer notes — {tacName}</h3>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-4">
        <Lock className="h-3 w-3" aria-hidden="true" /> Visible to EnterpriseSG staff only — the partner (TAC) can never see these.
      </div>
      <div className="flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add an internal observation about this partner…" className="rounded-md" data-testid="note-input" />
        <Button onClick={add} disabled={busy || !text.trim()} className="rounded-md self-end" data-testid="add-note-button"><Send className="h-4 w-4 mr-1.5" aria-hidden="true" /> Add</Button>
      </div>
      <ul className="mt-4 space-y-3">
        {notes.length === 0 && <li className="text-sm text-muted-foreground">No internal notes yet.</li>}
        {notes.map((n) => (
          <li key={n.id} className="border-l-2 border-[#303E49] pl-3" data-testid={`note-${n.id}`}>
            <div className="text-sm">{n.note}</div>
            <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{n.author_name} · {n.author_role?.toUpperCase()} · {fmtDate(n.created_at)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuestionRow({ r, idx, existing, canValidate, onSave }) {
  const [comment, setComment] = useState(existing?.comment || "");
  const [vscore, setVscore] = useState(existing?.validator_score ?? "");
  useEffect(() => { setComment(existing?.comment || ""); setVscore(existing?.validator_score ?? ""); }, [existing]);

  return (
    <div className="bg-card border border-border rounded-md p-5" data-testid={`validate-question-${r.question_id}`}>
      <div className="flex items-start gap-3">
        <span className="font-mono text-xs text-muted-foreground mt-1">{idx + 1}.</span>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{r.pillar_name}</div>
              <p className="text-sm font-medium mt-0.5">{r.question_text}</p>
              {r.note && <p className="text-xs text-muted-foreground mt-1 italic">Partner note: {r.note}</p>}
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Self score</div>
              <div className="font-heading text-xl font-bold">{r.score}<span className="text-sm text-muted-foreground">/{r.max_score}</span></div>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-[120px_1fr] gap-3 items-start">
            <div>
              <Label className="text-xs">Validator score</Label>
              <Input type="number" min={0} max={r.max_score} value={vscore} disabled={!canValidate}
                onChange={(e) => setVscore(e.target.value === "" ? "" : Number(e.target.value))}
                className="mt-1 rounded-md" data-testid={`validator-score-${r.question_id}`} />
            </div>
            <div>
              <Label className="text-xs">Validation comment</Label>
              <Textarea value={comment} disabled={!canValidate} onChange={(e) => setComment(e.target.value)}
                className="mt-1 rounded-md" placeholder="Add a per-question comment…" data-testid={`validator-comment-${r.question_id}`} />
            </div>
          </div>
          {canValidate && (
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant="outline" className="rounded-md"
                onClick={() => onSave(r.question_id, comment, vscore === "" ? null : vscore)}
                data-testid={`save-comment-${r.question_id}`}>Save comment</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateFollowupDialog({ submission, onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "medium" });
  const [saving, setSaving] = useState(false);
  if (!["validator", "am", "admin"].includes(user.role)) return null;

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/followups", {
        assessment_id: submission.assessment_id, submission_id: submission.id,
        tac_id: submission.tac_id, ...form,
      });
      toast.success("Follow-up created and assigned");
      setOpen(false);
      setForm({ title: "", description: "", due_date: "", priority: "medium" });
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-md" data-testid="create-followup-button"><Flag className="h-4 w-4 mr-1.5" aria-hidden="true" /> Follow-up</Button>
      </DialogTrigger>
      <DialogContent className="rounded-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Create governance follow-up</DialogTitle>
          <DialogDescription>Assigns an action to {submission.tac_name} with a due date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-md" data-testid="followup-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" data-testid="followup-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 rounded-md" data-testid="followup-duedate-input" /></div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1 rounded-md" data-testid="followup-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
          <Button onClick={create} disabled={saving || !form.title || !form.due_date} className="rounded-md" data-testid="confirm-followup-button">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
